import { useState, useEffect, useCallback, useRef } from 'react';
import supabase from '../supabase/supabase';

export function useEntries(userId) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchTimeoutRef = useRef(null);
  const skipNextRealtimeRef = useRef(false);

  // Fetch entries from Supabase
  const fetchEntries = useCallback(async () => {
    if (!userId) {
      setEntries([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('entries')
        .select('*')
        .eq('user_id', userId)
        .order('timestamp', { ascending: true });

      if (error) {
        console.error('Fetch entries error:', error);
        throw error;
      }


      // Convert timestamp strings to Date objects with validation
      const entriesWithDates = (data || []).map(entry => {
        const timestamp = entry.timestamp ? new Date(entry.timestamp) : new Date();
        // Check if date is valid
        if (isNaN(timestamp.getTime())) {
          console.warn('Invalid timestamp for entry:', entry.id);
          return { ...entry, timestamp: new Date() };
        }
        return { ...entry, timestamp };
      });

      setEntries(entriesWithDates);
      setError(null);
    } catch (err) {
      console.error('Error fetching entries:', err);
      setError(err.message);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Initial fetch
  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Subscribe to realtime changes (for multi-device sync only)
  useEffect(() => {
    if (!userId) return;


    const channel = supabase
      .channel('entries_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'entries',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {

          // Skip refetch if we just made our own change (optimistic update already handled it)
          if (skipNextRealtimeRef.current) {
            skipNextRealtimeRef.current = false;
            return;
          }

          // Only refetch for changes from other devices/sessions
          if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
          }

          // Refetch to sync changes from other devices
          fetchTimeoutRef.current = setTimeout(() => {
            fetchEntries();
          }, 300);
        }
      )
      .subscribe();

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      supabase.removeChannel(channel);
    };
  }, [userId, fetchEntries]);

  // Add a new entry
  const addEntry = async (entryData) => {
    if (!userId) {
      console.error('No userId provided');
      return { error: 'User not authenticated' };
    }

    try {
      const insertData = {
        user_id: userId,
        content: entryData.content,
        sender: entryData.sender || 'user',
        type: entryData.type || 'note',
        completed: entryData.type === 'todo' ? (entryData.completed ?? false) : false,
        tags: entryData.tags || [],
        timestamp: entryData.timestamp || new Date().toISOString(),
        // Include task enhancement fields
        priority: entryData.priority || null,
        deadline: entryData.deadline || null,
        recurring: entryData.recurring || null,
      };


      const { data, error } = await supabase
        .from('entries')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('Insert error:', error);
        throw error;
      }


      // Optimistically add to local state for instant UI update
      const newEntry = {
        ...data,
        timestamp: new Date(data.timestamp),
        completed: data.completed ?? false,
        type: data.type || 'note',
        priority: data.priority || null,
        deadline: data.deadline || null,
        recurring: data.recurring || null,
      };

      // Skip next realtime refetch since we're updating optimistically
      skipNextRealtimeRef.current = true;

      // Add and sort entries immediately by timestamp for correct positioning
      setEntries(prev => {
        const updated = [...prev, newEntry];
        return updated.sort((a, b) => {
          const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
          const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
          return timeA - timeB;
        });
      });

      return { data, error: null };
    } catch (err) {
      console.error('Error adding entry:', err);
      return { data: null, error: err.message };
    }
  };

  // Update an entry
  const updateEntry = async (entryId, updates) => {
    if (!userId) return { error: 'User not authenticated' };

    // Optimistically update local state
    skipNextRealtimeRef.current = true;
    setEntries(prev => prev.map(entry =>
      entry.id === entryId
        ? { ...entry, ...updates }
        : entry
    ));

    try {
      const { data, error } = await supabase
        .from('entries')
        .update(updates)
        .eq('id', entryId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;


      // Update with server data, preserving all fields including completed, priority, deadline, recurring
      setEntries(prev => prev.map(entry =>
        entry.id === entryId
          ? {
              ...data,
              timestamp: new Date(data.timestamp),
              completed: data.completed ?? false,
              priority: data.priority || null,
              deadline: data.deadline || null,
              recurring: data.recurring || null,
            }
          : entry
      ));

      return { data, error: null };
    } catch (err) {
      console.error('Error updating entry:', err);
      // Rollback on error - refetch to get correct state
      fetchEntries();
      return { data: null, error: err.message };
    }
  };

  // Delete an entry
  const deleteEntry = async (entryId) => {
    if (!userId) return { error: 'User not authenticated' };

    // Optimistically remove from local state immediately
    const previousEntries = entries;
    skipNextRealtimeRef.current = true;
    setEntries(prev => prev.filter(entry => entry.id !== entryId));

    try {
      const { error } = await supabase
        .from('entries')
        .delete()
        .eq('id', entryId)
        .eq('user_id', userId);

      if (error) throw error;

      return { error: null };
    } catch (err) {
      console.error('Error deleting entry:', err);
      // Rollback on error - restore previous state
      setEntries(previousEntries);
      return { error: err.message };
    }
  };

  // Toggle todo completion
  const toggleTodoCompletion = async (entryId, currentCompleted) => {
    return updateEntry(entryId, { completed: !currentCompleted });
  };

  return {
    entries,
    loading,
    error,
    addEntry,
    updateEntry,
    deleteEntry,
    toggleTodoCompletion,
    refetch: fetchEntries,
  };
}